import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { FormGroup } from '@euler/components/form';
import { Input, useBaseInputStyle } from '@euler/components/form/Input';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Range } from '@euler/model/common';
import { dateFromString } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useMemo, useRef } from 'react';
import {
  StyleProp,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const DateRangeField = memo(
  ({
    value,
    label,
    style,
    onLowerChange,
    onUpperChange,
  }: {
    label: string;
    value: Range<string> | undefined;
    style?: StyleProp<ViewStyle>;
    onLowerChange?: (value: string | undefined) => void;
    onUpperChange?: (value: string | undefined) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <FormGroup label={label} style={style}>
        <StackLayout
          direction="row"
          spacing={rpx(8)}
          divider={<Typography.Text>→</Typography.Text>}
          alignItems="center"
        >
          <DateCell
            value={value?.start}
            placeholder="开始时间"
            onChange={onLowerChange}
            style={{ flex: 1 }}
          />
          <DateCell
            value={value?.end}
            placeholder="结束时间"
            onChange={onUpperChange}
            style={{ flex: 1 }}
          />
        </StackLayout>
      </FormGroup>
    );
  },
);

const DateCell = memo(
  ({
    value,
    placeholder,
    style,
    onChange,
  }: {
    value: string | undefined;
    placeholder: string;
    style?: StyleProp<ViewStyle>;
    onChange?: (value: string | undefined) => void;
  }) => {
    const inputRef = useRef<TextInput>(null);

    const [isDateTimePickerVisible, showDateTimePicker, hideDateTimePicker] =
      useDateTimePicker();

    const onConfirm = usePersistFn((date: Date) => {
      onChange?.(date.toISOString());
      hideDateTimePicker();
    });

    const inputValue = value ? formatDateTime(value) : '';
    const dateValue = useMemo(
      () => (value ? dateFromString(value) : undefined),
      [value],
    );

    const { placeholderTextColor } = useBaseInputStyle();

    const onChangeText = usePersistFn((text: string) => {
      if (!text) {
        onChange?.(undefined);
      }
    });

    return (
      <TouchableOpacity style={style} onPress={showDateTimePicker}>
        <Input
          inputRef={inputRef}
          placeholderTextColor={placeholderTextColor}
          value={inputValue}
          placeholder={placeholder}
          editable={false}
          focusable={false}
          clearButtonMode="always"
          onChangeText={onChangeText}
        />
        <View
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            right: inputValue ? 28 : 0,
          }}
        />
        <DateTimePicker
          isVisible={isDateTimePickerVisible}
          mode="datetime"
          date={dateValue}
          onConfirm={onConfirm}
          onCancel={hideDateTimePicker}
          confirmTextIOS="确认"
          cancelTextIOS="关闭"
        />
      </TouchableOpacity>
    );
  },
);
